import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import type * as Y from "yjs";

import type { CollaborationProviderLike } from "../editor/types";
import { RECONNECT_DELAY_MS, collaborationUrl, reconnectDelayMs } from "./session";

/**
 * The adapter between our hook and `@hocuspocus/provider`. It exists so the hook can be tested
 * against a fake: everything React-facing talks to `SessionHandle`, never to Hocuspocus.
 */

export type SessionEvent =
  | { type: "status"; status: "connecting" | "connected" | "disconnected" }
  | { type: "authenticated"; scope: "read-write" | "readonly" }
  | { type: "close"; code: number }
  | { type: "awareness"; states: Map<number, Record<string, unknown>> };

export interface SessionHandle {
  provider: CollaborationProviderLike;
  /** The awareness client id of this connection, so the presence row can find itself. */
  clientId: () => number | null;
  /** Publishes one awareness field for the local user. */
  setField: (field: string, value: unknown) => void;
  /** Points the socket at a freshly issued token before the next reconnect attempt. */
  refreshToken: (token: string) => void;
  /** Stops reconnecting for good; used on the terminal `4999`. */
  stop: () => void;
  destroy: () => void;
}

export interface ConnectOptions {
  documentId: string;
  ydoc: Y.Doc;
  token: string;
  /** Called on every reconnect attempt so the session can present a fresh token (§15). */
  getToken: () => Promise<string>;
  onEvent: (event: SessionEvent) => void;
  origin?: string;
}

export type Connector = (options: ConnectOptions) => SessionHandle;

/** The real connector. Everything it knows about Hocuspocus stays inside this function. */
export function connectHocuspocus(options: ConnectOptions): SessionHandle {
  const name = `document.${options.documentId}`;
  const url = (token: string): string =>
    collaborationUrl({
      documentId: options.documentId,
      token,
      ...(options.origin === undefined ? {} : { origin: options.origin }),
    });

  let pendingRetry: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const clearRetry = (): void => {
    if (pendingRetry !== null) {
      clearTimeout(pendingRetry);
      pendingRetry = null;
    }
  };

  /**
   * Reconnection, taken out of the library's hands.
   *
   * Its own loop cannot pace our refusals. A refusal arrives *after* the socket has opened —
   * the server accepts the upgrade and then closes with a code — and an opened socket clears
   * the library's retry state, so each refusal schedules a fresh attempt at the flat base delay
   * while the previous retry chain is still running. The chains multiply: measured against a
   * refusing endpoint, that is upwards of a hundred attempts a second within seconds.
   *
   * So the socket is taken out of its loop on every close and given exactly one attempt back,
   * after the delay the close code earns (§15). The delay is computed from that close alone and
   * never accumulated, which is what makes a connection that works again cost nothing: the next
   * ordinary drop reconnects at the base delay however long the last refusal made it wait.
   */
  const scheduleRetry = (event: { code: number; reason?: string }): void => {
    clearRetry();
    socket.disconnect();
    if (stopped) {
      return;
    }
    pendingRetry = setTimeout(() => {
      pendingRetry = null;
      if (!stopped) {
        void socket.connect();
      }
    }, reconnectDelayMs(event));
  };

  const socket = new HocuspocusProviderWebsocket({
    url: url(options.token),
    // The base delay for an attempt that never opens at all — a server that is down — where the
    // library's exponential backoff with jitter does work, capped at 30 s (§15).
    delay: RECONNECT_DELAY_MS,
    factor: 2,
    maxDelay: 30_000,
    minDelay: 500,
    jitter: true,
    maxAttempts: 0,
    // Registered on the socket rather than the provider because it has to run before the
    // socket's own close handling, which is what would otherwise schedule the retry.
    onClose: ({ event }) => {
      scheduleRetry(event);
    },
  });

  const provider = new HocuspocusProvider({
    websocketProvider: socket,
    name,
    document: options.ydoc,
    token: options.getToken,
    onStatus: ({ status }) => {
      options.onEvent({ type: "status", status });
    },
    onAuthenticated: ({ scope }) => {
      options.onEvent({ type: "authenticated", scope });
    },
    onClose: ({ event }) => {
      options.onEvent({ type: "close", code: event.code });
    },
  });
  provider.attach();
  provider.awareness?.on("change", () => {
    const awareness = provider.awareness;
    if (awareness) {
      options.onEvent({ type: "awareness", states: awareness.getStates() });
    }
  });

  return {
    provider,
    clientId: () => provider.awareness?.clientID ?? null,
    setField: (field, value) => {
      provider.setAwarenessField(field, value);
    },
    refreshToken: (token) => {
      socket.setConfiguration({ url: url(token) });
    },
    stop: () => {
      stopped = true;
      clearRetry();
      socket.disconnect();
    },
    destroy: () => {
      stopped = true;
      clearRetry();
      try {
        provider.destroy();
        socket.destroy();
      } catch {
        // The socket was already closing; there is nothing left to tear down.
      }
    },
  };
}
